<template>
  <div class="SectionTitle">
    <component
      :is="headingTag"
      v-if="$slots.heading"
      :class="['SectionTitle__heading', `SectionTitle__heading--${headingTag}`]"
    >
      <slot name="heading" />
    </component>

    <component
      :is="titleTag"
      v-if="$slots.title || title"
      :class="[
        'SectionTitle__title',
        `SectionTitle__title--${titleSize}`,
        `SectionTitle__title--${titleTag}`,
      ]"
    >
      <slot name="title">{{ title }}</slot>
    </component>

    <p v-if="$slots.description" class="SectionTitle__description">
      <slot name="description" />
    </p>
  </div>
</template>

<script setup lang="ts">
withDefaults(
  defineProps<{
    /** Title text to display when no title slot is provided */
    title?: string
    titleSize?: 'sm' | 'md'
    headingTag?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    titleTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  }>(),
  {
    title: '',
    titleSize: 'md',
    headingTag: 'h3',
    titleTag: 'h2',
  },
)
</script>

<style lang="scss" scoped>
.SectionTitle {
  .SectionTitle__heading {
    @include font-size-16px;
    color: $color-ui-brand-700;
    font-weight: 600;
    margin-bottom: 0.75rem;
  }
  .SectionTitle__title {
    font-weight: 600;
    color: $color-ui-gray-cool-900;
    letter-spacing: -2%;

    &--sm {
      font-size: 1.5rem;
      line-height: 1.875rem;

      @include md {
        font-size: 1.875rem;
        line-height: 2.375rem;
      }
    }

    &--md {
      font-size: 1.875rem;
      line-height: 2.375rem;

      @include md {
        font-size: 2.25rem;
        line-height: 2.75rem;
      }
    }

    &--h1 {
      color: $color-ui-gray-cool-900;
      font-weight: 600;
      letter-spacing: -1.2px;
      font-size: var(--Font-size-display-sm, 30px);
      font-style: normal;
      line-height: var(--Line-height-display-sm, 38px);

      @include md {
        font-size: var(--Font-size-display-md, 36px);
        line-height: var(--Line-height-display-md, 44px); /* 122.222% */
      }

      @include lg {
        font-size: 60px;
        line-height: var(--Line-height-display-xl, 72px);
      }
    }
  }

  .SectionTitle__description {
    @include font-size-16px();
    color: $color-ui-gray-cool-600;
    margin-top: 1rem;

    @include md {
      @include font-size-18px();
    }
  }
}
</style>
